import React, {Fragment} from 'react'

import Guid from '../../../utils/guid.js'
import _ from 'underscore'

import {Button, IconButton, Modal, ModalHeader, ModalActions, H6, Popup, Row, P, ToolTip, Card} from '../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, Toggle, CurrencyInput, FloatInput, Select, Steps, MetricInput, AddressSearch} from '../UI/forms/index.js'
import {Customer, Reseller} from '../../../types/types.js'
import {OrderPriceTableLocation} from 'server/functions/resellers/resellers.types.js'
import DividerHeader from '@components/UI/surfaces/DividerHeader.js'
import Languages from '@utils/Languages.js'
import t from 'src/helpers/Languages.js'
import {getZoneDetermination, ZoneDetermination} from '@utils/distanceZone.js'
import naturalSortBy from 'src/utils/naturalSortBy.js'
import dbCodes from 'server/dbCodes.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import type {PopupRef} from '../UI/overlays/Popup.js'
import OrderConditionGroupsEditor from '../conditions/OrderConditionGroupsEditor.js'

type OrderFee = Reseller['settings']['orders']['prices'][0]['fees'][number]
type OrderPriceTable = Reseller['settings']['orders']['prices'][0]
type PriceTable = OrderPriceTable

interface PriceTableModalProps {
    reseller: Reseller
    customers: {[id: string]: Customer}
    onSubmit: (priceTable: PriceTable) => void
    onRemove?: (id: string) => void
}

interface PriceTableModalState {
    modalIsOpen: boolean
    priceTable: PriceTable
    newPriceTable: boolean
    validation: {
        name?: boolean
        startLocation?: boolean
        endLocation?: boolean
    }
}

export default class PriceTableModal extends React.Component<PriceTableModalProps, PriceTableModalState> {
    initialState: PriceTableModalState
    popup: PopupRef

    constructor(props) {
        super(props)

        this.initialState = {
            modalIsOpen: false,
            priceTable: {
                id: '',
                name: '',
                distanceFeeType: 'fixed',

                // Fixed
                price: '0,00',

                // Distance
                startRate: '0,00',
                distanceVehicleType: 'car',
                startLocation: {type: 'order'},
                endLocation: {type: 'order'},
                distanceFee: '0,00',
                startDistance: 0,
                fuelFeeType: '',
                fuelFeePercent: '',

                // Delivery zones
                deliveryZones: [],
                zoneFees: {},

                // Distance zones
                distanceZones: [],
                useProducts: false,
                usePriceScales: false,
                useProgressivePriceScales: false,
                usePriceScalesPerDay: false,
                useDeliveryAreas: false,
                zoneDetermination: 'straightLine',

                // Fees
                feeExtraStop: '0,00',
                nrOfStopsIncluded: 2,
                feeForPickupAddress: '0,00',
                feeForDeliveryAddress: '0,00',
                feeForExtraCollo: '0,00',
                nrOfColliIncluded: 1,
                colliTypeNrOfColliIncluded: 0,
                colliTypes: [{
                    fee: '0,00',
                    type: ''
                }],
                pickupAndDeliveryOptions: [{
                    fee: '0,00',
                    option: ''
                }],
                fees: [],

                minimumRate: '0,00',
                rounding: ''
            },
            newPriceTable: undefined,
            validation: {}
        }

        this.state = structuredClone(this.initialState)
    }

    open(priceTable?: PriceTable) {
        const {reseller} = this.props

        const newPriceTable = !priceTable

        priceTable = priceTable ?
            JSON.parse(JSON.stringify(priceTable)) :
            {
                ...this.initialState.priceTable,
                id: Guid.generate()
            }

        priceTable.deliveryZones = priceTable.deliveryZones || []
        priceTable.deliveryZones = priceTable.deliveryZones.filter((id) => {
            return !!_.findWhere(reseller.settings.deliveryZones, {id})
        })

        priceTable.startLocation = priceTable.startLocation || {type: 'order'}
        priceTable.endLocation = priceTable.endLocation || {type: 'order'}

        priceTable.pickupAndDeliveryOptions = priceTable.pickupAndDeliveryOptions || []
        priceTable.colliTypes = priceTable.colliTypes || []

        if (priceTable.distanceFeeType === 'distanceZones' && !priceTable.distanceZones?.length) {
            priceTable.distanceZones = [newDistanceZone()]
        }

        this.setState({
            modalIsOpen: true,
            newPriceTable,
            priceTable
        })

        if (!priceTable.fees) {
            priceTable.fees = []
        }
    }

    close() {
        this.setState(this.initialState)
    }

    onChangeValue(key, event) {
        const priceTable = {...this.state.priceTable}

        if (key === 'rounding') {
            priceTable[key] = parseFloat(event.target.value)
        } else if (key === 'startDistance' || key === 'nrOfStopsIncluded' || key === 'nrOfColliIncluded' || key === 'colliTypeNrOfColliIncluded') {
            priceTable[key] = parseInt(event.target.value)
        } else {
            priceTable[key] = event.target.value
        }

        // A zones table without zones shows nothing to fill in, so start with the first zone
        if (key === 'distanceFeeType' && priceTable.distanceFeeType === 'distanceZones' && !priceTable.distanceZones?.length) {
            priceTable.distanceZones = [newDistanceZone()]
        }

        this.setState({priceTable})
    }

    onChangeChecked(key, event) {
        const priceTable = {...this.state.priceTable}

        priceTable[key] = event.target.checked

        this.setState({priceTable})
    }

    onChangeLocation(key: 'startLocation' | 'endLocation', event) {
        const priceTable = {...this.state.priceTable}
        const value: string = event.target.value

        let location: OrderPriceTableLocation = {type: 'order'}

        if (value === 'start') {
            location = {type: 'start'}
        } else if (value === 'companyAddress') {
            location = {type: 'companyAddress'}
        } else if (value.startsWith('branch-')) {
            location = {type: 'branch', branchId: value.replace('branch-', '')}
        } else if (value === 'custom') {
            location = {type: 'custom', address: priceTable[key]?.address}
        }

        priceTable[key] = location

        this.setState({priceTable, validation: {...this.state.validation, [key]: false}})
    }

    onChangeLocationAddress(key: 'startLocation' | 'endLocation', address) {
        const priceTable = {...this.state.priceTable}

        priceTable[key] = {
            ...priceTable[key],
            address: {
                name: address.name || '',
                attention: '',
                street: address.street || '',
                nr: address.nr || '',
                addition: address.addition || '',
                street2: address.street2 || '',
                postalCode: address.postalCode || '',
                city: address.city || '',
                country: address.country || '',
                email: '',
                phone: '',
                position: address.position
            }
        }

        this.setState({priceTable, validation: {...this.state.validation, [key]: false}})
    }

    getLocationOptions(key: 'startLocation' | 'endLocation') {
        const {reseller} = this.props
        const {priceTable} = this.state

        const branches = branchesAvailable(reseller) ? reseller.settings.branches || [] : []

        const options: {value: string, title: string}[] = [
            {value: 'order', title: key === 'startLocation' ? 'Eerste adres in rit' : 'Laatste adres in rit'}
        ]

        if (key === 'endLocation') {
            options.push({value: 'start', title: 'Terug naar start'})
        }

        // The own company address is only offered when there are no branches, unless it is already selected
        if (!branches.length || priceTable[key]?.type === 'companyAddress') {
            options.push({value: 'companyAddress', title: reseller.settings?.accountName || reseller.name})
        }

        branches.map((branch) => {
            options.push({value: `branch-${branch.id}`, title: branch.name})
        })

        options.push({value: 'custom', title: 'Anders'})

        return options
    }

    getLocationValue(location: OrderPriceTableLocation) {
        if (location?.type === 'branch') {
            return `branch-${location.branchId}`
        }

        return location?.type || 'order'
    }

    // useProducts and useColliType are mutually exclusive ways of picking the base tariff, controlled by a single select
    onChangeTariffBasis(event) {
        const priceTable = {...this.state.priceTable}

        priceTable.useProducts = event.target.value === 'products'
        priceTable.useColliType = event.target.value === 'colliType'

        // the flat fee-per-collo-type surcharge is superseded by useColliType's per-zone/per-table base tariff, so clear it to avoid double-charging
        if (priceTable.useColliType) {
            priceTable.colliTypes = []
        }

        this.setState({priceTable})
    }

    onAddPickupDeliveryOption() {
        const {priceTable} = this.state

        priceTable.pickupAndDeliveryOptions.push({
            fee: '0,00',
            option: ''
        })

        this.setState({priceTable})
    }

    onAddColliType() {
        const {priceTable} = this.state

        if (!priceTable.colliTypes) {
            priceTable.colliTypes = []
        }

        priceTable.colliTypes.push({
            fee: '0,00',
            type: ''
        })

        this.setState({priceTable})
    }

    onChangePickupDeliveryOption(key, index, event) {
        const {priceTable} = this.state

        if (priceTable.pickupAndDeliveryOptions?.[index]) {
            priceTable.pickupAndDeliveryOptions[index][key] = event.target.value
        }

        this.setState({priceTable})
    }

    onRemovePickupDeliveryOption(index) {
        const {priceTable} = this.state

        priceTable.pickupAndDeliveryOptions.splice(index, 1)

        this.setState({priceTable})
    }

    onChangeColliType(key, index, event) {
        const {priceTable} = this.state

        if (priceTable.colliTypes?.[index]) {
            priceTable.colliTypes[index][key] = event.target.value
        }

        this.setState({priceTable})
    }

    onRemoveColliType(index) {
        const {priceTable} = this.state

        if (priceTable.colliTypes?.[index]) {
            priceTable.colliTypes.splice(index, 1)
        }

        this.setState({priceTable})
    }

    onChangeDeliveryZones(event) {
        const priceTable = {...this.state.priceTable}

        priceTable.deliveryZones = event.target.values

        this.setState({priceTable})
    }

    onChangeZoneFee(key, event) {
        const priceTable = {...this.state.priceTable}

        const keys = key.split('-')

        if (!priceTable.zoneFees) {
            priceTable.zoneFees = {}
        }

        priceTable.zoneFees[`${keys[0]}-${keys[1]}`] = event.target.value
        priceTable.zoneFees[`${keys[1]}-${keys[0]}`] = event.target.value

        this.setState({priceTable})
    }

    onValidate() {
        const priceTable = {...this.state.priceTable}
        const validation: PriceTableModalState['validation'] = {}
        let id = ''
        let valid = true

        if (!priceTable.name) {
            id = 'name'
            validation.name = true
            valid = false
        }

        if (priceTable.distanceFeeType === 'distance') {
            ['startLocation', 'endLocation'].map((key) => {
                if (priceTable[key]?.type === 'custom' && !priceTable[key].address?.street) {
                    id = id || `searchAddress${key}`
                    validation[key] = true
                    valid = false
                }
            })
        }

        if (!valid) {
            const element = document.getElementById(id)
            if (element) {
                element.focus()
            }
        }

        this.setState({validation})
        return valid
    }

    onSubmit(event) {
        event.preventDefault()

        if (this.onValidate()) {
            this.props.onSubmit(this.state.priceTable)
            this.close()
        }
    }

    onAddFee() {
        const priceTable = {...this.state.priceTable}

        const id = Math.random().toString(16).substring(2, 14)

        priceTable.fees = [...priceTable.fees, {id, description: '', price: '0,00'}]

        this.setState({priceTable})
    }

    onChangeFee(key, id, event) {
        const priceTable = {...this.state.priceTable}

        priceTable.fees = priceTable.fees.map((fee) => fee.id === id ? {...fee, [key]: event.target.value} : fee)

        this.setState({priceTable})
    }

    onChangeFeeChecked(key, id, event) {
        const priceTable = {...this.state.priceTable}

        priceTable.fees = priceTable.fees.map((fee) => fee.id === id ? {...fee, [key]: event.target.checked} : fee)

        this.setState({priceTable})
    }

    onRemoveFee(id) {
        const priceTable = {...this.state.priceTable}

        priceTable.fees = priceTable.fees.filter((fee) => fee.id !== id)

        this.setState({priceTable})
    }

    onChangeFeeConditions(id: string, conditionGroups: OrderFee['conditionGroups'], matchAllConditions: boolean) {
        const priceTable = {...this.state.priceTable}

        priceTable.fees = priceTable.fees.map((fee) => fee.id === id ? {...fee, conditionGroups, matchAllConditions} : fee)

        this.setState({priceTable})
    }

    render() {
        const {modalIsOpen, priceTable, newPriceTable, validation} = this.state
        const {reseller, customers} = this.props

        const availableColliTypes = reseller.settings.orders.colliTypes || []

        const zoneDetermination = getZoneDetermination(priceTable)
        const zoneDeterminationOptions: {value: ZoneDetermination, title: string}[] = [
            {value: 'straightLine', title: t('zoneDeterminationStraightLine')},
            {value: 'carDistance', title: t('zoneDeterminationCarDistance')},
            {value: 'bikeDistance', title: t('zoneDeterminationBikeDistance')},
            {value: 'deliveryAreas', title: t('deliveryAreas')}
        ]

        return (
            <Modal show={modalIsOpen} onClose={this.close.bind(this)}>
                <ModalHeader title={newPriceTable ? 'Nieuwe tarieftabel' : 'Bewerk tarieftabel'} onClose={this.close.bind(this)} style={{marginBottom: 0}} />

                <br />
                <form onSubmit={this.onSubmit.bind(this)}>

                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Input
                            id='name'
                            label='Naam'
                            value={priceTable.name}
                            isInvalid={validation.name}
                            onChange={this.onChangeValue.bind(this, 'name')}
                        />

                        <Select
                            label='Locatie berekening op basis van'
                            value={priceTable.distanceFeeType}
                            options={[
                                {value: 'zones', title: 'Bezorggebied matrix'},
                                {value: 'distance', title: 'Kilometerberekening'},
                                {value: 'fixed', title: 'Vaste prijs'},
                                {value: 'distanceZones', title: 'Zones'}
                            ]}
                            onChange={this.onChangeValue.bind(this, 'distanceFeeType')}
                        />

                        {priceTable.distanceFeeType === 'fixed' &&
                            <>
                                <Select
                                    label='Tarief op basis van'
                                    value={priceTable.useProducts ? 'products' : priceTable.useColliType ? 'colliType' : ''}
                                    options={[
                                        {value: '', title: 'Alleen locatie'},
                                        {value: 'colliType', title: 'Collo type'},
                                        {value: 'products', title: 'Producten'}
                                    ]}
                                    onChange={this.onChangeTariffBasis.bind(this)}
                                />

                                {!priceTable.useProducts && !priceTable.useColliType &&
                                    <CurrencyInput
                                        label={Languages.price['NL']}
                                        value={priceTable.price}
                                        onChange={this.onChangeValue.bind(this, 'price')}
                                    />
                                }

                                {priceTable.useProducts &&
                                    <Card shadow style={{padding: 12}}>
                                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                            {naturalSortBy(reseller.settings.orders.products || [], 'name').map((product, index) => {
                                                if (!priceTable.products) {
                                                    priceTable.products = {}
                                                }

                                                if (!priceTable.products[product.id]) {
                                                    priceTable.products[product.id] = {price: '0,00', priceScales: []}
                                                }

                                                return (
                                                    <FormRow key={product.id} style={{alignItems: 'center', marginTop: index > 0 ? 12 : 0}}>
                                                        <div style={{flex: 1, minWidth: 0}}>
                                                            <H6 style={{marginBottom: 12}} ellipsis>{product.name}</H6>
                                                        </div>

                                                        <CurrencyInput
                                                            label='Prijs'
                                                            value={priceTable.products[product.id]?.price || '0,00'}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                const priceTable = {...this.state.priceTable}

                                                                priceTable.products = priceTable.products || {}
                                                                priceTable.products[product.id] = priceTable.products[product.id] || {price: '0,00', priceScales: []}
                                                                priceTable.products[product.id].price = event.target.value

                                                                this.setState({priceTable})
                                                            }}
                                                        />
                                                    </FormRow>
                                                )
                                            })}
                                        </FormStack>
                                    </Card>
                                }

                                {priceTable.useColliType &&
                                    <Card shadow style={{padding: 12}}>
                                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                            <FormRow>
                                                <Steps
                                                    label={t('priceTableNrOfColliIncluded')}
                                                    info={t('priceTableColliTypeNrOfColliIncludedInfo')}
                                                    value={priceTable.colliTypeNrOfColliIncluded || 0}
                                                    onChange={this.onChangeValue.bind(this, 'colliTypeNrOfColliIncluded')}
                                                    min={0}
                                                />

                                                <div style={{flex: 1}} />
                                            </FormRow>

                                            {naturalSortBy(availableColliTypes, 'name').map((colliType, index) => {
                                                if (!priceTable.colliTypePrices) {
                                                    priceTable.colliTypePrices = {}
                                                }

                                                if (!priceTable.colliTypePrices[colliType.id]) {
                                                    priceTable.colliTypePrices[colliType.id] = {price: '0,00', priceScales: []}
                                                }

                                                return (
                                                    <FormRow key={colliType.id} style={{alignItems: 'center', marginTop: index > 0 ? 12 : 0}}>
                                                        <div style={{flex: 1, minWidth: 0}}>
                                                            <H6 style={{marginBottom: 12}} ellipsis>{colliType.name}</H6>
                                                        </div>

                                                        <CurrencyInput
                                                            label='Prijs'
                                                            value={priceTable.colliTypePrices[colliType.id]?.price || '0,00'}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                const priceTable = {...this.state.priceTable}

                                                                priceTable.colliTypePrices = priceTable.colliTypePrices || {}
                                                                priceTable.colliTypePrices[colliType.id] = priceTable.colliTypePrices[colliType.id] || {price: '0,00', priceScales: []}
                                                                priceTable.colliTypePrices[colliType.id].price = event.target.value

                                                                this.setState({priceTable})
                                                            }}
                                                        />
                                                    </FormRow>
                                                )
                                            })}
                                        </FormStack>
                                    </Card>
                                }
                            </>
                        }

                        {priceTable.distanceFeeType === 'distance' &&
                            <>
                                <Select
                                    label={t('distanceVehicleType')}
                                    value={priceTable.distanceVehicleType || 'car'}
                                    options={[
                                        {value: 'car', title: t('car')},
                                        {value: 'bike', title: t('bike')}
                                    ]}
                                    onChange={this.onChangeValue.bind(this, 'distanceVehicleType')}
                                />

                                <FormRow>
                                    {(['startLocation', 'endLocation'] as const).map((key) => {
                                        return (
                                            <FormStack key={key} style={{flex: 1}}>
                                                <Select
                                                    label={key === 'startLocation' ? t('start') : t('end')}
                                                    value={this.getLocationValue(priceTable[key])}
                                                    options={this.getLocationOptions(key)}
                                                    onChange={this.onChangeLocation.bind(this, key)}
                                                />

                                                {priceTable[key]?.type === 'custom' &&
                                                    <AddressSearch
                                                        id={key}
                                                        address={priceTable[key].address}
                                                        isInvalid={validation[key]}
                                                        onChange={this.onChangeLocationAddress.bind(this, key)}
                                                    />
                                                }
                                            </FormStack>
                                        )
                                    })}
                                </FormRow>

                                <FormRow>
                                    <CurrencyInput
                                        label={t('distanceFee')}
                                        value={priceTable.distanceFee}
                                        onChange={this.onChangeValue.bind(this, 'distanceFee')}
                                    />

                                    <Input
                                        type='number'
                                        label={t('startDistance')}
                                        value={priceTable.startDistance || 0}
                                        onChange={this.onChangeValue.bind(this, 'startDistance')}
                                    />
                                </FormRow>

                                <FormRow>
                                    <FloatInput
                                        label={t('detourFactorPercentage')}
                                        info={t('detourFactorPercentageInfo')}
                                        value={priceTable.detourFactorPercentage}
                                        onChange={this.onChangeValue.bind(this, 'detourFactorPercentage')}
                                        append='%'
                                    />

                                    <div style={{flex: 1}} />
                                </FormRow>

                                <FormRow>
                                    <Select
                                        allowEmpty
                                        label={t('fuelFeeType')}
                                        value={priceTable.fuelFeeType}
                                        options={[
                                            {value: 'orderPrice', title: t('fuelFeeTypeOrderPrice')},
                                            {value: 'distanceFee', title: t('fuelFeeTypeDistanceFee')}
                                        ]}
                                        onChange={this.onChangeValue.bind(this, 'fuelFeeType')}
                                    />

                                    {priceTable.fuelFeeType &&
                                        <FloatInput
                                            label={t('percentage')}
                                            value={priceTable.fuelFeePercent}
                                            onChange={this.onChangeValue.bind(this, 'fuelFeePercent')}
                                            append='%'
                                        />
                                    }
                                </FormRow>
                            </>
                        }

                        {priceTable.distanceFeeType === 'zones' && reseller.settings.deliveryZones &&
                            <FormStack>
                                <Select
                                    label='Bezorggebieden'
                                    options={reseller.settings.deliveryZones.map((zone) => {
                                        return {value: zone.id, title: zone.name}
                                    })}
                                    values={priceTable.deliveryZones || []}
                                    onChange={this.onChangeDeliveryZones.bind(this)}
                                />

                                <div key='scrollContainer' style={{display: 'flex', flexDirection: 'column', gap: formSpacing.rowGap, width: 760, overflowX: 'auto'}}>
                                    <Row style={{marginTop: 12}}>
                                        <Row style={{paddingLeft: 6, paddingRight: 6, width: 120, flexShrink: 0, minWidth: 'auto'}} />

                                        {priceTable.deliveryZones.map((zoneColumn) => {
                                            const deliveryZone = _.findWhere(reseller.settings.deliveryZones, {id: zoneColumn})

                                            if (!deliveryZone) {
                                                return
                                            }

                                            return (
                                                <Row key={deliveryZone.id} style={{paddingLeft: 6, paddingRight: 6, width: 120, flexShrink: 0, minWidth: 'auto'}}>
                                                    <ToolTip text={deliveryZone.name} style={{width: 112}}>
                                                        <P ellipsis>{deliveryZone.name}</P>
                                                    </ToolTip>
                                                </Row>
                                            )
                                        })}
                                    </Row>

                                    {priceTable.deliveryZones.map((zoneRow, rowIndex) => {
                                        const deliveryZone = _.findWhere(reseller.settings.deliveryZones, {id: zoneRow})

                                        if (!deliveryZone) {
                                            return
                                        }

                                        return (
                                            <Row key={zoneRow} style={{alignItems: 'center'}}>
                                                <Row style={{paddingLeft: 6, paddingRight: 6, width: 120, flexShrink: 0, minWidth: 'auto'}}>
                                                    <ToolTip text={deliveryZone.name} placement='right' style={{width: 112}}>
                                                        <P ellipsis>{deliveryZone.name}</P>
                                                    </ToolTip>
                                                </Row>

                                                {priceTable.deliveryZones.map((zoneColumn, columnIndex) => {
                                                    const deliveryZone = _.findWhere(reseller.settings.deliveryZones, {id: zoneColumn})

                                                    if (!deliveryZone || columnIndex > rowIndex) {
                                                        return
                                                    }

                                                    return (
                                                        <Row key={`${zoneColumn}-${zoneRow}`} style={{paddingLeft: 6, paddingRight: 6, width: 120, flexShrink: 0, minWidth: 'auto'}}>
                                                            <CurrencyInput
                                                                label='Prijs'
                                                                value={priceTable.zoneFees ? priceTable.zoneFees[`${zoneColumn}-${zoneRow}`] || 0 : 0}
                                                                onChange={this.onChangeZoneFee.bind(this, `${zoneColumn}-${zoneRow}`)}
                                                            />
                                                        </Row>

                                                    )
                                                })}
                                            </Row>
                                        )
                                    })}
                                </div>
                            </FormStack>
                        }

                        {priceTable.distanceFeeType === 'distanceZones' &&
                            <>
                                <Select
                                    label='Tarief op basis van'
                                    value={priceTable.useProducts ? 'products' : priceTable.useColliType ? 'colliType' : ''}
                                    options={[
                                        {value: '', title: 'Alleen locatie'},
                                        {value: 'colliType', title: 'Collo type'},
                                        {value: 'products', title: 'Producten'}
                                    ]}
                                    onChange={this.onChangeTariffBasis.bind(this)}
                                />

                                {!priceTable.useColliType &&
                                    <>
                                        <Toggle
                                            label={Languages.usePriceScales['NL']}
                                            checked={priceTable.usePriceScales}
                                            onChange={this.onChangeChecked.bind(this, 'usePriceScales')}
                                        />

                                        {priceTable.usePriceScales &&
                                            <>
                                                <Toggle
                                                    label={Languages.useProgressivePriceScales['NL']}
                                                    info='Bij gebruik van progressieve staffels vallen de eerste ritten altijd in de laagste staffel, en een volgend deel van de ritten pas in een volgende staffel. Elke staffel wordt gevuld voor een volgend tarief gaat gelden.'
                                                    checked={priceTable.useProgressivePriceScales}
                                                    onChange={this.onChangeChecked.bind(this, 'useProgressivePriceScales')}
                                                />

                                                <Toggle
                                                    label={Languages.usePriceScalesPerDay['NL']}
                                                    checked={priceTable.usePriceScalesPerDay}
                                                    onChange={this.onChangeChecked.bind(this, 'usePriceScalesPerDay')}
                                                />
                                            </>
                                        }
                                    </>
                                }

                                <Select
                                    label={t('zoneDetermination')}
                                    info={t('zoneDeterminationInfo')}
                                    value={zoneDetermination}
                                    options={zoneDeterminationOptions}
                                    onChange={(event) => {
                                        const value = event.target.value as ZoneDetermination

                                        // useDeliveryAreas stays in sync, so a table saved now still works for code from before zoneDetermination
                                        this.setState({priceTable: {...this.state.priceTable, zoneDetermination: value, useDeliveryAreas: value === 'deliveryAreas'}})
                                    }}
                                />

                                {priceTable.distanceZones?.map((zone, index) => {
                                    if (!priceTable.distanceZones[index].priceScales) {
                                        priceTable.distanceZones[index].priceScales = [{
                                            price: '0,00',
                                            maxAmount: Number.MAX_SAFE_INTEGER
                                        }]
                                    }

                                    return (
                                        <Card shadow key={index} style={{padding: 12}}>
                                            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                                <FormRow style={{alignItems: 'center'}}>
                                                    <Input
                                                        label={t('zoneName')}
                                                        value={zone.name}
                                                        onChange={(event) => {
                                                            const priceTable = {...this.state.priceTable}

                                                            priceTable.distanceZones[index].name = event.target.value
                                                            this.setState({priceTable})
                                                        }}
                                                    />

                                                    {zoneDetermination === 'deliveryAreas' ?
                                                        <Select
                                                            label={t('deliveryAreas')}
                                                            values={zone.deliveryZones || []}
                                                            options={reseller.settings.deliveryZones.map((deliveryZone) => {
                                                                // A delivery area can be chosen in one zone per price table. Areas already in more zones
                                                                // stay where they are until changed.
                                                                const inOtherZone = priceTable.distanceZones.some((otherZone, otherIndex) => otherIndex !== index && otherZone.deliveryZones?.includes(deliveryZone.id))

                                                                return {value: deliveryZone.id, title: deliveryZone.name, hidden: inOtherZone && !zone.deliveryZones?.includes(deliveryZone.id)}
                                                            })}
                                                            onChange={(event) => {
                                                                const priceTable = {...this.state.priceTable}

                                                                priceTable.distanceZones[index].deliveryZones = event.target.values
                                                                this.setState({priceTable})
                                                            }}
                                                        /> :
                                                        <MetricInput
                                                            label={zoneDeterminationOptions.find((option) => option.value === zoneDetermination)?.title}
                                                            value={zone.maxDistance}
                                                            divisionFactor={1000}
                                                            onChange={(event) => {
                                                                const priceTable = {...this.state.priceTable}

                                                                priceTable.distanceZones[index].maxDistance = +event.target.value
                                                                this.setState({priceTable})
                                                            }}
                                                        />
                                                    }

                                                    {!priceTable.useProducts && !priceTable.useColliType && !priceTable.usePriceScales &&
                                                        <CurrencyInput
                                                            label={t('price')}
                                                            value={zone.price || '0,00'}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                const priceTable = {...this.state.priceTable}

                                                                priceTable.distanceZones[index].price = event.target.value

                                                                this.setState({priceTable})
                                                            }}
                                                        />
                                                    }

                                                    <IconButton
                                                        onClick={() => {
                                                            const priceTable = {...this.state.priceTable}
                                                            priceTable.distanceZones.splice(index, 1)
                                                            this.setState({priceTable})
                                                        }}
                                                    >
                                                        <i className='mdi mdi-delete' />
                                                    </IconButton>
                                                </FormRow>

                                                {priceTable.useProducts &&
                                                    <>
                                                        {naturalSortBy(reseller.settings.orders.products || [], 'name').map((product) => {
                                                            if (!priceTable.distanceZones[index].products[product.id]) {
                                                                priceTable.distanceZones[index].products[product.id] = {
                                                                    price: '0,00',
                                                                    priceScales: [{
                                                                        price: '0,00',
                                                                        maxAmount: Number.MAX_SAFE_INTEGER
                                                                    }]
                                                                }
                                                            }

                                                            if (!priceTable.distanceZones[index].products[product.id].priceScales) {
                                                                priceTable.distanceZones[index].products[product.id].priceScales = [{
                                                                    price: '0,00',
                                                                    maxAmount: Number.MAX_SAFE_INTEGER
                                                                }]
                                                            }

                                                            return (
                                                                <FormStack key={product.id} style={{marginTop: formSpacing.rowGap}}>
                                                                    <FormRow key={product.id} style={{alignItems: 'center', marginTop: formSpacing.rowGap}}>
                                                                        <div style={{flex: 1, minWidth: 0}}>
                                                                            <H6 style={{marginBottom: 12}} ellipsis>{product.name}</H6>
                                                                        </div>

                                                                        {!priceTable.usePriceScales &&
                                                                            <CurrencyInput
                                                                                label='Prijs'
                                                                                value={zone.products[product.id]?.price || '0,00'}
                                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                                    const priceTable = {...this.state.priceTable}

                                                                                    if (!priceTable.distanceZones[index].products[product.id]) {
                                                                                        priceTable.distanceZones[index].products[product.id] = {
                                                                                            price: '0,00',
                                                                                            priceScales: []
                                                                                        }
                                                                                    }

                                                                                    priceTable.distanceZones[index].products[product.id].price = event.target.value

                                                                                    this.setState({priceTable})
                                                                                }}
                                                                            />
                                                                        }

                                                                        <div style={{width: 42}} />
                                                                    </FormRow>

                                                                    {priceTable.usePriceScales &&
                                                                        <>
                                                                            <FormRow key={product.id} style={{alignItems: 'center', marginTop: formSpacing.rowGap, flexWrap: 'wrap'}}>
                                                                                {priceTable.distanceZones[index].products[product.id]?.priceScales?.map((priceScale, priceScaleIndex) => {
                                                                                    const previousMax = priceScaleIndex === 0 ? 0 : priceTable.distanceZones[index].products[product.id].priceScales[priceScaleIndex - 1].maxAmount
                                                                                    const isLastPriceScale = priceScaleIndex === priceTable.distanceZones[index].products[product.id].priceScales.length - 1

                                                                                    return (
                                                                                        <div key={`zone-${index}${product.id}-priceScale${priceScaleIndex}`} style={{display: 'flex', position: 'relative', width: 108}}>
                                                                                            <CurrencyInput
                                                                                                label={isLastPriceScale ? `${previousMax}+ st` : priceScale.maxAmount === previousMax + 1 ? `${priceScale.maxAmount} st` : `${previousMax + 1} - ${priceScale.maxAmount} st`}
                                                                                                value={priceScale.price || '0,00'}
                                                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                                                    const priceTable = {...this.state.priceTable}

                                                                                                    priceTable.distanceZones[index].products[product.id].priceScales[priceScaleIndex].price = event.target.value

                                                                                                    this.setState({priceTable})
                                                                                                }}
                                                                                            />

                                                                                            {!isLastPriceScale &&
                                                                                                <IconButton
                                                                                                    style={{position: 'absolute', right: 0, top: 6}}
                                                                                                    icon='mdi mdi-pencil'
                                                                                                    onClick={async () => {
                                                                                                        this.popup.setInputs([
                                                                                                            {
                                                                                                                type: 'number',
                                                                                                                name: 'maxAmount',
                                                                                                                label: 'Tot',
                                                                                                                value: priceScale.maxAmount
                                                                                                            }
                                                                                                        ])

                                                                                                        this.popup.setButtonBottom({
                                                                                                            label: Languages.delete['NL'],
                                                                                                            variant: 'text',
                                                                                                            onClick: () => {
                                                                                                                const priceTable = {...this.state.priceTable}
                                                                                                                priceTable.distanceZones[index].products[product.id].priceScales.splice(priceScaleIndex, 1)
                                                                                                                this.setState({priceTable})
                                                                                                                this.popup.close()
                                                                                                            },
                                                                                                            style: {marginRight: 'auto'}
                                                                                                        })

                                                                                                        const values = await this.popup.open('Bewerk staffel', '')
                                                                                                        if (!values) return
                                                                                                        const maxAmount = parseInt(values.maxAmount)

                                                                                                        const priceTable = {...this.state.priceTable}

                                                                                                        const priceScales = priceTable.distanceZones[index].products[product.id].priceScales

                                                                                                        priceScales[priceScaleIndex].maxAmount = maxAmount

                                                                                                        priceTable.distanceZones[index].products[product.id].priceScales = sortPriceScales(priceScales)

                                                                                                        this.setState({priceTable})
                                                                                                        this.popup.close()
                                                                                                    }}
                                                                                                />
                                                                                            }
                                                                                        </div>
                                                                                    )
                                                                                })}

                                                                                <Button
                                                                                    variant='outline'
                                                                                    icon='mdi mdi-plus'
                                                                                    onClick={() => {
                                                                                        const priceTable = {...this.state.priceTable}

                                                                                        const priceScales = priceTable.distanceZones[index].products[product.id].priceScales

                                                                                        priceScales.push({
                                                                                            price: '0,00',
                                                                                            maxAmount: priceScales.length > 1 ? priceScales[priceScales.length - 2].maxAmount + 1 : 1
                                                                                        })

                                                                                        priceTable.distanceZones[index].products[product.id].priceScales = sortPriceScales(priceScales)

                                                                                        this.setState({priceTable})
                                                                                    }}
                                                                                />
                                                                            </FormRow>
                                                                        </>
                                                                    }
                                                                </FormStack>
                                                            )
                                                        })}
                                                    </>
                                                }

                                                {priceTable.useColliType &&
                                                    <>
                                                        <FormRow style={{marginTop: formSpacing.rowGap}}>
                                                            <Steps
                                                                label={t('priceTableNrOfColliIncluded')}
                                                                info={t('priceTableColliTypeNrOfColliIncludedInfo')}
                                                                value={zone.nrOfColliIncluded || 0}
                                                                onChange={(event) => {
                                                                    const priceTable = {...this.state.priceTable}

                                                                    priceTable.distanceZones[index].nrOfColliIncluded = parseInt(String(event.target.value)) || 0
                                                                    this.setState({priceTable})
                                                                }}
                                                                min={0}
                                                            />

                                                            <div style={{flex: 1}} />
                                                        </FormRow>

                                                        {naturalSortBy(availableColliTypes, 'name').map((colliType) => {
                                                            if (!priceTable.distanceZones[index].colliTypePrices) {
                                                                priceTable.distanceZones[index].colliTypePrices = {}
                                                            }

                                                            if (!priceTable.distanceZones[index].colliTypePrices[colliType.id]) {
                                                                priceTable.distanceZones[index].colliTypePrices[colliType.id] = {
                                                                    price: '0,00',
                                                                    priceScales: []
                                                                }
                                                            }

                                                            return (
                                                                <FormRow key={colliType.id} style={{alignItems: 'center', marginTop: formSpacing.rowGap}}>
                                                                    <div style={{flex: 1, minWidth: 0}}>
                                                                        <H6 style={{marginBottom: 12}} ellipsis>{colliType.name}</H6>
                                                                    </div>

                                                                    <CurrencyInput
                                                                        label='Prijs'
                                                                        value={zone.colliTypePrices?.[colliType.id]?.price || '0,00'}
                                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                            const priceTable = {...this.state.priceTable}

                                                                            if (!priceTable.distanceZones[index].colliTypePrices) {
                                                                                priceTable.distanceZones[index].colliTypePrices = {}
                                                                            }

                                                                            if (!priceTable.distanceZones[index].colliTypePrices[colliType.id]) {
                                                                                priceTable.distanceZones[index].colliTypePrices[colliType.id] = {
                                                                                    price: '0,00',
                                                                                    priceScales: []
                                                                                }
                                                                            }

                                                                            priceTable.distanceZones[index].colliTypePrices[colliType.id].price = event.target.value

                                                                            this.setState({priceTable})
                                                                        }}
                                                                    />

                                                                    <div style={{width: 42}} />
                                                                </FormRow>
                                                            )
                                                        })}
                                                    </>
                                                }

                                                {!priceTable.useProducts && !priceTable.useColliType && priceTable.usePriceScales &&
                                                    <>
                                                        <FormRow style={{alignItems: 'center', marginTop: formSpacing.rowGap, flexWrap: 'wrap'}}>
                                                            {priceTable.distanceZones[index]?.priceScales?.map((priceScale, priceScaleIndex) => {
                                                                const previousMax = priceScaleIndex === 0 ? 0 : priceTable.distanceZones[index].priceScales[priceScaleIndex - 1].maxAmount
                                                                const isLastPriceScale = priceScaleIndex === priceTable.distanceZones[index].priceScales.length - 1

                                                                return (
                                                                    <div key={`zone-${index}-priceScale${priceScaleIndex}`} style={{display: 'flex', position: 'relative', width: 108}}>
                                                                        <CurrencyInput
                                                                            label={isLastPriceScale ? `${previousMax}+ st` : `${previousMax} - ${priceScale.maxAmount} st`}
                                                                            value={priceScale.price || '0,00'}
                                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                                const priceTable = {...this.state.priceTable}

                                                                                priceTable.distanceZones[index].priceScales[priceScaleIndex].price = event.target.value

                                                                                this.setState({priceTable})
                                                                            }}
                                                                        />

                                                                        {!isLastPriceScale &&
                                                                            <IconButton
                                                                                style={{position: 'absolute', right: 0, top: 6}}
                                                                                icon='mdi mdi-pencil'
                                                                                onClick={async () => {
                                                                                    this.popup.setInputs([
                                                                                        {
                                                                                            type: 'number',
                                                                                            name: 'maxAmount',
                                                                                            label: 'Tot',
                                                                                            value: priceScale.maxAmount
                                                                                        }
                                                                                    ])

                                                                                    this.popup.setButtonBottom({
                                                                                        label: Languages.delete['NL'],
                                                                                        variant: 'text',
                                                                                        onClick: () => {
                                                                                            const priceTable = {...this.state.priceTable}
                                                                                            priceTable.distanceZones[index].priceScales.splice(priceScaleIndex, 1)
                                                                                            this.setState({priceTable})
                                                                                            this.popup.close()
                                                                                        },
                                                                                        style: {marginRight: 'auto'}
                                                                                    })

                                                                                    const values = await this.popup.open('Bewerk staffel', '')
                                                                                    if (!values) return
                                                                                    const maxAmount = parseInt(values.maxAmount)

                                                                                    const priceTable = {...this.state.priceTable}

                                                                                    const priceScales = priceTable.distanceZones[index].priceScales

                                                                                    priceScales[priceScaleIndex].maxAmount = maxAmount

                                                                                    priceTable.distanceZones[index].priceScales = sortPriceScales(priceScales)

                                                                                    this.setState({priceTable})
                                                                                    this.popup.close()
                                                                                }}
                                                                            />
                                                                        }
                                                                    </div>
                                                                )
                                                            })}

                                                            <Button
                                                                variant='outline'
                                                                icon='mdi mdi-plus'
                                                                onClick={() => {
                                                                    const priceTable = {...this.state.priceTable}

                                                                    const priceScales = priceTable.distanceZones[index].priceScales

                                                                    priceScales.push({
                                                                        price: '0,00',
                                                                        maxAmount: priceScales.length > 1 ? priceScales[priceScales.length - 2].maxAmount + 1 : 1
                                                                    })

                                                                    priceTable.distanceZones[index].priceScales = sortPriceScales(priceScales)

                                                                    this.setState({priceTable})
                                                                }}
                                                            />
                                                        </FormRow>
                                                    </>
                                                }
                                            </FormStack>
                                        </Card>
                                    )
                                })}

                                <Button
                                    variant='outline'
                                    icon='mdi mdi-plus'
                                    onClick={() => {
                                        const priceTable = {...this.state.priceTable}

                                        if (!priceTable.distanceZones) {
                                            priceTable.distanceZones = []
                                        }

                                        priceTable.distanceZones.push(newDistanceZone())

                                        this.setState({priceTable})
                                    }}
                                />
                            </>
                        }
                    </FormStack>

                    <DividerHeader
                        defaultHidden={true}
                        style={{marginBottom: 18, marginTop: 24}}
                        title='Toeslagen'
                    >
                        <H6 style={{marginBottom: 12}}>Stops</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <CurrencyInput
                                    label='Tarief per stop'
                                    value={priceTable.feeExtraStop}
                                    onChange={this.onChangeValue.bind(this, 'feeExtraStop')}
                                />

                                <Steps
                                    label='Aantal stops inbegrepen'
                                    value={priceTable.nrOfStopsIncluded}
                                    onChange={this.onChangeValue.bind(this, 'nrOfStopsIncluded')}
                                    min={0}
                                />
                            </FormRow>

                            <FormRow>
                                <CurrencyInput
                                    label='Toeslag per ophaaladres'
                                    value={priceTable.feeForPickupAddress}
                                    onChange={this.onChangeValue.bind(this, 'feeForPickupAddress')}
                                />

                                <CurrencyInput
                                    label='Toeslag per bezorgadres'
                                    value={priceTable.feeForDeliveryAddress}
                                    onChange={this.onChangeValue.bind(this, 'feeForDeliveryAddress')}
                                />
                            </FormRow>
                        </FormStack>

                        {/* useColliType includes colli per zone instead, feeForExtraCollo and nrOfColliIncluded stay stored for switching back */}
                        {!priceTable.useColliType &&
                            <>
                                <br />
                                <H6 style={{marginBottom: 12}}>Colli</H6>
                                <FormRow style={{marginBottom: formSpacing.rowGap}}>
                                    <CurrencyInput
                                        label='Tarief per collo'
                                        value={priceTable.feeForExtraCollo}
                                        onChange={this.onChangeValue.bind(this, 'feeForExtraCollo')}
                                    />

                                    <Steps
                                        label={t('priceTableNrOfColliIncluded')}
                                        value={priceTable.nrOfColliIncluded}
                                        onChange={this.onChangeValue.bind(this, 'nrOfColliIncluded')}
                                        min={0}
                                    />
                                </FormRow>
                            </>
                        }

                        {!priceTable.useColliType &&
                            <>
                                <br />
                                <H6 style={{marginBottom: 12}}>Tarief per collo type</H6>

                                <FormStack>
                                    {(priceTable.colliTypes || []).map((colliType, index) => (
                                        <FormRow key={`colli-type-${index}`}>
                                            <CurrencyInput
                                                label='Tarief per type collo'
                                                value={colliType.fee}
                                                onChange={(event) => this.onChangeColliType('fee', index, event)}
                                            />

                                            <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                                                <Select
                                                    label='Selecteer collo type'
                                                    options={availableColliTypes.map((type) => ({
                                                        value: type.id,
                                                        title: type.name
                                                    })).filter((opt) => {
                                                        const i = (priceTable.colliTypes || []).findIndex((type) => type.type === opt.value)
                                                        return i <= -1 || i === index
                                                    })
                                                    }
                                                    value={colliType.type}
                                                    onChange={(event) => this.onChangeColliType('type', index, event)}
                                                />

                                                <IconButton
                                                    icon='mdi mdi-delete'
                                                    onClick={this.onRemoveColliType.bind(this, index)}
                                                />
                                            </FormRow>
                                        </FormRow>
                                    ))}

                                    <Button onClick={this.onAddColliType.bind(this)} variant='outline-white' icon='mdi mdi-plus' />
                                </FormStack>
                            </>
                        }

                        <br />
                        <H6 style={{marginBottom: 12}}>Ophaal- en bezorgopties</H6>

                        <FormStack>
                            {priceTable.pickupAndDeliveryOptions.map((option, index) => (
                                <FormRow key={`option-${index}`}>
                                    <CurrencyInput
                                        label='Tarief per optie per adres'
                                        value={option.fee}
                                        onChange={(event) => this.onChangePickupDeliveryOption('fee', index, event)}
                                    />

                                    <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                                        <Select
                                            label='Selecteer ophaal-/bezorgoptie'
                                            options={[
                                                ...Object.entries(dbCodes.pickupAndDeliveryOptions).map(([key, value]) => ({
                                                    value: key,
                                                    title: value
                                                })),
                                                ...(reseller.settings.orders.customOptions || []).map((option) => ({
                                                    value: option.id,
                                                    title: option.name,
                                                    icon: 'mdi mdi-circle',
                                                    iconColor: option.color,
                                                    showIconInValue: true
                                                }))
                                            ].filter((opt) => {
                                                const i = priceTable.pickupAndDeliveryOptions.findIndex((option) => option.option === opt.value)
                                                return i <= -1 || i === index
                                            })
                                            }
                                            value={option.option}
                                            onChange={(event) => this.onChangePickupDeliveryOption('option', index, event)}
                                        />

                                        <IconButton
                                            icon='mdi mdi-delete'
                                            onClick={this.onRemovePickupDeliveryOption.bind(this, index)}
                                        />
                                    </FormRow>
                                </FormRow>
                            ))}

                            <Button onClick={this.onAddPickupDeliveryOption.bind(this)} variant='outline-white' icon='mdi mdi-plus' />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 24}}>Eigen toeslagen</H6>

                        {priceTable.fees.map((fee) => (
                            <Card key={`fee${fee.id}`} style={{marginBottom: 12, padding: 16}}>
                                <FormStack>
                                    <FormRow>
                                        <CurrencyInput
                                            label='Prijs'
                                            value={fee.price}
                                            onChange={this.onChangeFee.bind(this, 'price', fee.id)}
                                        />

                                        <Input
                                            label='Naam'
                                            value={fee.description}
                                            onChange={this.onChangeFee.bind(this, 'description', fee.id)}
                                        />

                                        <IconButton onClick={this.onRemoveFee.bind(this, fee.id)}>
                                            <i className='mdi mdi-delete' />
                                        </IconButton>
                                    </FormRow>

                                    <FormRow style={{marginTop: formSpacing.rowGap}}>
                                        <Toggle
                                            label='Automatisch toepassen'
                                            checked={fee.auto}
                                            onChange={this.onChangeFeeChecked.bind(this, 'auto', fee.id)}
                                        />

                                        <div style={{flex: 1}} />
                                    </FormRow>

                                    {fee.auto &&
                                        <OrderConditionGroupsEditor
                                            conditionGroups={fee.conditionGroups || []}
                                            matchAll={!!fee.matchAllConditions}
                                            customers={customers || {}}
                                            reseller={reseller}
                                            onChange={(conditionGroups, matchAllConditions) => this.onChangeFeeConditions(fee.id, conditionGroups, matchAllConditions)}
                                        />
                                    }
                                </FormStack>
                            </Card>
                        ))}

                        <Button onClick={this.onAddFee.bind(this)} variant='outline-white' icon='mdi mdi-plus' />
                    </DividerHeader>

                    <DividerHeader
                        defaultHidden={true}
                        style={{marginBottom: 18, marginTop: 24}}
                        title='Overige'
                    >
                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                {(['', 'distance'].includes(priceTable.distanceFeeType) || !priceTable.distanceFeeType) &&
                                    <CurrencyInput
                                        label='Starttarief'
                                        value={priceTable.startRate}
                                        onChange={this.onChangeValue.bind(this, 'startRate')}
                                    />
                                }

                                <CurrencyInput
                                    label='Minimum prijs'
                                    value={priceTable.minimumRate}
                                    onChange={this.onChangeValue.bind(this, 'minimumRate')}
                                />
                            </FormRow>

                            <Select
                                label='Afronding totaal'
                                allowEmpty
                                value={priceTable.rounding}
                                options={[
                                    {value: 1, title: 'Hele euro\'s'},
                                    {value: 2.5, title: 'Veelvoud van €2,50'},
                                    {value: 5, title: 'Veelvoud van €5,00'},
                                    {value: 10, title: 'Veelvoud van €10,00'},
                                    {value: 5.05, title: 'Veelvoud van €5,00 - €0,05'},
                                    {value: 10.05, title: 'Veelvoud van €10,00 - €0,05'}
                                ]}
                                onChange={this.onChangeValue.bind(this, 'rounding')}
                            />
                        </FormStack>
                    </DividerHeader>

                    <ModalActions>

                        <Button type='submit'>
                            Opslaan
                        </Button>
                    </ModalActions>
                </form>

                <Popup
                    ref={(ref) => this.popup = ref}
                />
            </Modal>
        )
    }
}

function newDistanceZone() {
    return {
        name: '',
        maxDistance: 0,
        nrOfColliIncluded: 0,
        products: {},
        colliTypePrices: {}
    }
}

function sortPriceScales(priceScales) {
    // Sort by maxAmount
    const sortedPriceScales = priceScales.toSorted((a, b) => a.maxAmount - b.maxAmount)

    // If there are multiple price scales with the same maxAmount, increase the maxAmount of the first one by 1 until all maxAmounts are unique. This is necessary to be able to edit the maxAmount of a price scale without having duplicate maxAmounts.
    sortedPriceScales.forEach((priceScale, index) => {
        const sameMaxAmountIndex = sortedPriceScales.findIndex((p, i) => p.maxAmount === priceScale.maxAmount && index !== i)

        if (sameMaxAmountIndex > -1) {
            sortedPriceScales[sameMaxAmountIndex].maxAmount = sortedPriceScales[sameMaxAmountIndex].maxAmount + 1
        }
    })

    sortedPriceScales[sortedPriceScales.length - 1].maxAmount = Number.MAX_SAFE_INTEGER

    return sortedPriceScales
}
